import { useCallback } from "react";

import { useFormContext } from "react-hook-form";

import { config } from "@/config";
import {
  IIAnalysisSuccessResponse,
  IIRoofImageResponse,
  IV1AnalysisPostRequest,
  v1AnalysisPost,
  v1ImageSessionIdGet,
} from "@/contract/generated/electrify-v1";
import { useApi } from "@/hooks/useApi";
import { isError } from "@/utils";
import { getSessionId } from "@/utils/sessionId";

export const useRATProcessing = () => {
  const { setValue } = useFormContext();
  const { call } = useApi();

  const roofAnalysis = async (
    sessionId: string
  ): Promise<IIAnalysisSuccessResponse | Error> => {
    const request: IV1AnalysisPostRequest = { sessionId };

    const response = await call(() =>
      v1AnalysisPost(config.apiBasePath, request)
    );
    return Promise.resolve(response);
  };

  const getRoofImage = async (
    sessionId: string
  ): Promise<IIRoofImageResponse | Error> => {
    const response = await call(() =>
      v1ImageSessionIdGet(config.apiBasePath, {
        sessionId,
      })
    );
    return Promise.resolve(response);
  };

  const analyzeRoof = useCallback(async () => {
    const roofAnalysisResult = await roofAnalysis(getSessionId());

    if (isError(roofAnalysisResult)) {
      setValue("degradedExperience", true);
    } else {
      const roofAnalysisResponse =
        roofAnalysisResult as IIAnalysisSuccessResponse;

      if (
        roofAnalysisResponse.panelNumber &&
        roofAnalysisResponse.panelNumber < 10
      ) {
        setValue("degradedExperience", true);
      }

      setValue("roofAnalysisResult", roofAnalysisResponse);

      const roofImageResult = await getRoofImage(getSessionId());

      if (isError(roofImageResult)) {
        setValue("degradedExperience", true);
      } else {
        const roofImageResponse = roofImageResult as IIRoofImageResponse;

        setValue("roofImageBase64", roofImageResponse.base64Image);

        setValue(
          "accurassiRoofArea",
          roofAnalysisResponse.suitableRoofArea || 0
        );
        setValue(
          "accurassiStoreys",
          roofAnalysisResponse.levels?.toString() || "1"
        );
        setValue(
          "accurassiHasSolarPanel",
          roofAnalysisResponse.hasSolarPanels || false
        );
        setValue("accurassiRoofMaterial", roofAnalysisResponse.roofMaterial);
      }
    }

    setValue("canService", true);
  }, []);

  return {
    analyzeRoof,
  };
};
